import { FunctionComponent, useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
	faCircleCheck,
	faDownload,
	faFileExcel,
	faTrash,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Modal } from "@/components/Modals/Modal";
import { Spinner } from "@/components/Spinner/Spinner";

import {
	useDeleteCsvEnrichmentMutation,
	useLazyDownloadCsvEnrichmentQuery,
} from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { classNames } from "@/utils/classNames";

import { ScrapeRequest } from "../../../CustomEvent/schema/scrapeRequest";
import { CsvEnrichment } from "../../../CsvEnrichment/schema/csvEnrichment";
import {
	buildScrapedFileRows,
	latestDuplicateCount,
	ScrapedFileRow,
	ScrapedFileStatus,
} from "./scrapedFiles";

export interface ScrapedFilesCardProps {
	csvEnrichments: CsvEnrichment[];
	scrapeRequests: ScrapeRequest[];
	listId: number;
}

const BADGE_STYLES: Record<ScrapedFileStatus, string> = {
	[ScrapedFileStatus.LATEST]: "bg-status-success-light text-status-success",
	[ScrapedFileStatus.SUPERSEDED]: "bg-background-light text-status-info",
	[ScrapedFileStatus.PROCESSING]: "bg-status-running-light text-status-running",
};

const BADGE_LABEL_KEY: Record<ScrapedFileStatus, string> = {
	[ScrapedFileStatus.LATEST]: "latest",
	[ScrapedFileStatus.SUPERSEDED]: "superseded",
	[ScrapedFileStatus.PROCESSING]: "processing",
};

/**
 * Every file delivered for a scraping source, newest first (PREC-3091). A pending re-scrape shows
 * as its own "Processing" row so the user can see that a file is on the way.
 */
export const ScrapedFilesCard: FunctionComponent<ScrapedFilesCardProps> = ({
	csvEnrichments,
	scrapeRequests,
	listId,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: { count?: number; date?: string }) =>
			t(`customEvent.rescrape.files.${key}`, options),
		[t]
	);

	const [download, { isLoading: downloadIsLoading }] = useLazyDownloadCsvEnrichmentQuery();
	const [deleteCsvEnrichment, { isLoading: deleteIsLoading }] = useDeleteCsvEnrichmentMutation();

	const [fileToDelete, setFileToDelete] = useState<CsvEnrichment | null>(null);

	const rows = useMemo(
		() => buildScrapedFileRows(scrapeRequests, csvEnrichments),
		[scrapeRequests, csvEnrichments]
	);

	const duplicateCount = useMemo(() => latestDuplicateCount(rows), [rows]);

	const handleDownload = (row: ScrapedFileRow) => async () => {
		const file = row.file;

		if (!file) {
			return;
		}

		const blob = await download(file.originalCsvEnrichmentId || file.id).unwrap();
		const url = window.URL.createObjectURL(blob);
		const anchor = document.createElement("a");

		anchor.href = url;
		anchor.download = `${file.name.replaceAll(".xlsx", "")}.xlsx`;
		document.body.appendChild(anchor);
		anchor.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(anchor);
	};

	const handleConfirmDelete = async () => {
		if (!fileToDelete) {
			return;
		}

		try {
			await deleteCsvEnrichment({ id: fileToDelete.id, listId }).unwrap();
			setFileToDelete(null);
		} catch (err) {
			console.error(err);
		}
	};

	const formatDate = (value?: string | null): string =>
		value ? new Date(value).toLocaleDateString() : "";

	return (
		<div className="border p-6 w-full rounded-xl border-1 border-border-light bg-white">
			<div className="text-smd text-text font-bold">{ts("title")}</div>
			<div className="text-ssm text-text-meta mb-4">{ts("subtitle")}</div>

			{rows.length === 0 ? (
				<div className="text-sm text-text-meta py-4">{ts("empty")}</div>
			) : (
				<table className="w-full border-collapse">
					<thead>
						<tr className="border-b border-border-light">
							<th className="text-left text-xs font-semibold text-text-meta uppercase pb-2 w-2/5">
								{ts("file")}
							</th>
							<th className="text-left text-xs font-semibold text-text-meta uppercase pb-2">
								{ts("records")}
							</th>
							<th className="text-left text-xs font-semibold text-text-meta uppercase pb-2">
								{ts("status")}
							</th>
							<th className="text-right text-xs font-semibold text-text-meta uppercase pb-2">
								{ts("actions")}
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => {
							const isProcessing = row.status === ScrapedFileStatus.PROCESSING;

							return (
								<tr key={row.key} className="border-b border-border-light last:border-b-0">
									<td className="py-3">
										<div className="flex flex-row items-center gap-2">
											<div
												className={classNames(
													"flex w-7 h-7 rounded-md items-center justify-center shrink-0",
													isProcessing ? "bg-status-running-light" : "bg-status-success-light"
												)}
											>
												<Icon
													className={classNames(
														"w-[14px] h-auto",
														isProcessing ? "text-status-running" : "text-status-success"
													)}
													icon={faFileExcel}
												/>
											</div>
											<div className="text-sm font-medium text-text truncate">
												{row.file?.name ||
													ts("requestedOn", { date: formatDate(row.request?.requestedAt) })}
											</div>
										</div>
									</td>
									<td className="py-3 text-sm text-text-meta">
										{isProcessing ? (
											<span className="flex flex-row items-center gap-2 text-status-running">
												<Spinner color="#D97706" />
												{ts("inProgress")}
											</span>
										) : (
											ts("recordCount", {
												count:
													row.file?.originalCsvEnrichment?.amountInCsv ??
													row.file?.amountInCsv ??
													0,
											})
										)}
									</td>
									<td className="py-3">
										<span
											className={classNames(
												"inline-flex items-center rounded-xl px-2 py-0.5 text-xs font-medium",
												BADGE_STYLES[row.status]
											)}
										>
											{ts(BADGE_LABEL_KEY[row.status])}
										</span>
									</td>
									<td className="py-3">
										<div className="flex flex-row justify-end">
											<button
												className="flex w-8 h-8 cursor-pointer items-center justify-center rounded-md text-text-meta hover:bg-background-light hover:text-text disabled:cursor-not-allowed disabled:opacity-40"
												disabled={!row.file || downloadIsLoading}
												title={ts("download")}
												type="button"
												onClick={handleDownload(row)}
											>
												<Icon className="w-[14px] h-auto" icon={faDownload} />
											</button>
											<button
												className="flex w-8 h-8 cursor-pointer items-center justify-center rounded-md text-text-meta hover:bg-status-error-light hover:text-status-error disabled:cursor-not-allowed disabled:opacity-40"
												disabled={!row.file || row.file.isImported}
												title={row.file?.isImported ? ts("deleteBlocked") : ts("delete")}
												type="button"
												onClick={() => setFileToDelete(row.file ?? null)}
											>
												<Icon className="w-[14px] h-auto" icon={faTrash} />
											</button>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			)}

			{fileToDelete && (
				<Modal
					handleClose={() => setFileToDelete(null)}
					handleSave={handleConfirmDelete}
					isLoading={deleteIsLoading}
					isOpened={!!fileToDelete}
					size="sm"
					submitButtonColor="error"
					submitButtonText={ts("delete")}
					title={ts("deleteTitle")}
				>
					<div className="text-sm text-text-meta">
						{ts("deleteConfirmation")} <b>{fileToDelete.name}</b>
					</div>
				</Modal>
			)}

			{!!duplicateCount && (
				<div className="mt-4 flex flex-row items-center gap-2 rounded-xl bg-status-success-light px-4 py-3 text-xs text-status-success">
					<Icon className="w-[14px] h-auto shrink-0" icon={faCircleCheck} />
					<span>{ts("duplicates", { count: duplicateCount })}</span>
				</div>
			)}
		</div>
	);
};
